import { useEffect, useState } from "react";
import { Download, Share } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { useLang } from "../i18n/LanguageContext";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const InstallButton = () => {
  const { t } = useLang();
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [ios] = useState(() => isIOS() && !isStandalone());

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setDeferred(null);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!deferred && !ios) return null;

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice.catch(() => null);
      setDeferred(null);
    } else setOpen(true);
  };

  return (
    <>
      <button
        data-testid="pwa-install-button"
        onClick={onClick}
        className="flex h-10 items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 text-[12px] font-semibold text-gold transition-colors duration-300 hover:bg-gold/20"
      >
        <Download className="h-3.5 w-3.5" strokeWidth={2.2} />
        {t.install.button}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="pwa-install-dialog" data-lenis-prevent className="max-w-sm rounded-[28px] border-white/10 bg-surface text-zinc-100">
          <DialogTitle className="font-display text-xl">{t.install.title}</DialogTitle>
          <DialogDescription className="text-zinc-400">{t.install.sub}</DialogDescription>
          <ol className="mt-2 space-y-3">
            {t.install.ios.map((step, i) => (
              <li key={step} className="flex items-center gap-3 rounded-2xl border border-white/5 bg-white/[0.03] p-3 text-sm text-zinc-200">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gold/15 font-mono text-xs text-gold">{i + 1}</span>
                {step}
                {i === 0 && <Share className="ml-auto h-4 w-4 text-gold" />}
              </li>
            ))}
          </ol>
          <button data-testid="pwa-install-dialog-ok" onClick={() => setOpen(false)} className="mt-2 h-12 rounded-full bg-gold font-semibold text-ink transition-opacity hover:opacity-90">
            {t.install.ok}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};
